import React, { Fragment, useEffect, useMemo, useReducer } from "react";
import styles from "./business-profile.module.css";
import StarIcon from "../Icons/star-icon";
import Avatar from "../CommonComponents/avatar/Avatar";
import { useDispatch, useSelector } from "react-redux";
import { reviewsListSelector } from "../../Reducers/reviewsListReducer";
import { useFetchCompanyInfosQuery } from "../../Apis/companyApi";
import { actions, initialState, reducer, useSetter } from "../Reviews/reviewListReducer";
import { getReviewsList } from "../../Actions/reviewsListAction";
import { cx } from "../../Util/classHelpers";
import LoadingSpinner from "../CommonComponents/loadingSpinner";
import { activeCompanySelector } from "../../Reducers/activeCompanyReducer";

const ReviewStars = ({ label, rating, max = 5 }) => {
  const stars = Array.from({ length: max }).map((_, index) => {
    const isFilled = index < rating;
    return (
      <StarIcon
        key={`${label}-star-${index}`}
        width={20}
        height={20}
        fill={isFilled ? "#EFB841" : "var(--color-Neutral-300, #D4D4D4)"}
        stroke={isFilled ? "#E2A013" : "var(--color-Neutral-300, #D4D4D4)"}
      />
    );
  });

  return (
    <div className={styles.ratingGroup}>
      <div className={styles.ratingLabel}>{label}</div>
      <div className={styles.ratingStars}>{stars}</div>
    </div>
  );
};

const SingleReview = ({ review }) => {
  const reviewRatings = useMemo(() => {
    let ratings = [];
    if (review.orderRating !== null) {
      ratings.push({label: "Order", rating: review.orderRating});
    }
    if (review.driverRating !== null) {
      ratings.push({label: "Driver", rating: review.driverRating});
    }
    if (review.serviceRating !== null) {
      ratings.push({label: "Service", rating: review.serviceRating});
    }
    return ratings;
  }, [review]);

  return (
    <div className={styles.reviewItem}>
      <Avatar name={review.customerName} size={36} />

      <div className={styles.reviewMain}>
        <div className={styles.reviewHeader}>
          <div className={styles.reviewName}>{review.customerName}</div>
          <div className={styles.reviewMetaRow}>
            <div className={styles.reviewMetaText}>{review.reviewTimeFormatted}</div>
            {
              review.orderNumber && (
                <>
                  <div className={styles.inlineSpacer}>•</div>
                  <div className={styles.reviewMetaText}>Order No: {review.orderNumber}</div>
                </>
              )
            }
            <div className={styles.inlineSpacer}>•</div>
            <div className={styles.reviewMetaText}>Phone: {review.customerPhoneNumber}</div>
            {
              review.carrierName && (
                <>
                  <div className={styles.inlineSpacer}>•</div>
                  <div className={styles.reviewMetaText}>Driver: {review.carrierName}</div>
                </>
              )
            }
          </div>
        </div>

        <div className={styles.ratingsRow}>
          {reviewRatings.map((rating, index) => (
            <Fragment key={rating.label}>
              {index > 0 && <div className={styles.verticalDivider20} />}
              <ReviewStars label={rating.label} rating={rating.rating} />
            </Fragment>
          ))}
        </div>

        {review.reviewText && (
          <div className={styles.reviewComment}>{review.reviewText}</div>
        )}
      </div>
    </div>
  );
};

const ReviewsTab = () => {
  const activeCompanyId = useSelector(activeCompanySelector);
  const reviewsList = useSelector(reviewsListSelector);
  const { data: partnerAccounts } = useFetchCompanyInfosQuery();

  const dispatch = useDispatch();

  const [state, reducerDispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (reviewsList.getTimeframe() !== state.timeFrame) {
      dispatch(getReviewsList(state.timeFrame));
    }
    reducerDispatch({ type: actions.SET_PAGE_SIZE, pageSize: 1000 });
    reducerDispatch({ type: actions.SET_COMPANY_IDS, companyIds: [activeCompanyId] });

  }, [state.timeFrame, activeCompanyId, reviewsList]);

  useSetter(reviewsList, reducerDispatch, actions.SET_ALL_REVIEWS);
  useSetter(partnerAccounts, reducerDispatch, actions.SET_ALL_COMPANIES);
  

  const showLoading =
    reviewsList.getTimeframe() !== state.timeFrame;

  return (
    <div className={cx(styles.reviewsRoot, showLoading && "disabled-div")}>
      <div className={styles.reviewsContainer}>
        {!showLoading && state.processedReviews.length === 0 && (
          <div className={styles.noReviewsContainer}>
            <div className={styles.noReviewsText}>No reviews found</div>
          </div>
        )}
        {state.processedReviews.map((review, index) => (
          <Fragment key={review.key}>
            <SingleReview review={review} />
            {index + 1 !== state.processedReviews.length && <div className={styles.dividerFull} />}
          </Fragment>
        ))}
      </div>

      {showLoading && (
        <div>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default ReviewsTab;
